/**
 * AI output renderer
 * ═══════════════════════════════════════════════════════════════
 *
 * Models reply in markdown. Rendered as plain text it looks like this:
 *
 *     ### Project: new pro - **Budget Utilization**: The project has an
 *     allocated budget of ₹23,434 but has not yet spent any funds.
 *
 * The structure was there the whole time — headings, bold labels, nested
 * bullets — and none of it was reaching the reader.
 *
 * WHY NOT react-markdown
 * ----------------------
 * It would work, but it is a dependency tree for a narrow job. What a model
 * actually emits is a small subset: headings, bold, italic, lists, code,
 * tables, links, rules, quotes. This handles that subset in ~200 lines.
 *
 * SAFETY
 * ------
 * Everything renders to React elements. There is no `dangerouslySetInnerHTML`
 * anywhere in this file, so model output cannot inject markup — the usual risk
 * when rendering generated content.
 */
import { useMemo } from 'react';

/** Inline: **bold**, *italic*, `code`, [text](url). Order matters. */
function renderInline(text, keyPrefix = 'i') {
  if (!text) return null;
  const out = [];
  let rest = String(text);
  let k = 0;

  const PATTERNS = [
    { re: /`([^`]+)`/, render: (m) => (
      <code key={`${keyPrefix}-${k}`} className="px-1.5 py-0.5 rounded-[5px] bg-[rgba(42,42,114,0.07)] text-[0.92em] font-mono text-[#2a2a72]">
        {m[1]}
      </code>
    ) },
    { re: /\*\*([^*]+)\*\*/, render: (m) => (
      <strong key={`${keyPrefix}-${k}`} className="font-semibold text-gray-900">{m[1]}</strong>
    ) },
    { re: /\*([^*\n]+)\*/, render: (m) => (
      <em key={`${keyPrefix}-${k}`} className="italic">{m[1]}</em>
    ) },
    { re: /\[([^\]]+)\]\(([^)\s]+)\)/, render: (m) => (
      <a key={`${keyPrefix}-${k}`} href={m[2]} target="_blank" rel="noopener noreferrer"
         className="text-[#1338be] underline underline-offset-2 hover:text-[#2a2a72]">
        {m[1]}
      </a>
    ) },
  ];

  // Walk left to right, always taking the earliest match so nesting behaves.
  let guard = 0;
  while (rest && guard++ < 500) {
    let best = null;
    for (const p of PATTERNS) {
      const m = p.re.exec(rest);
      if (m && (!best || m.index < best.m.index)) best = { m, p };
    }
    if (!best) break;
    if (best.m.index > 0) out.push(rest.slice(0, best.m.index));
    out.push(best.p.render(best.m));
    k += 1;
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  if (rest) out.push(rest);
  return out.length ? out : text;
}

/** Group consecutive lines into blocks, then render each. */
function parseBlocks(src) {
  const lines = String(src || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const body = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1;
      blocks.push({ type: 'code', lang, text: body.join('\n') });
      continue;
    }

    // Table — a header row followed by a separator of dashes
    if (/^\s*\|/.test(line) && /^\s*\|[\s:|-]+\|?\s*$/.test(lines[i + 1] || '')) {
      const rows = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) { rows.push(lines[i]); i += 1; }
      const cells = (r) => r.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      blocks.push({ type: 'table', head: cells(rows[0]), body: rows.slice(2).map(cells) });
      continue;
    }

    // Heading
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) { blocks.push({ type: 'heading', level: h[1].length, text: h[2] }); i += 1; continue; }

    // Horizontal rule
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) { blocks.push({ type: 'hr' }); i += 1; continue; }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const body = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { body.push(lines[i].replace(/^\s*>\s?/, '')); i += 1; }
      blocks.push({ type: 'quote', text: body.join(' ') });
      continue;
    }

    // List — bullets or numbers, with indent preserved for nesting
    if (/^\s*([-*+]|\d+[.)])\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
        const m = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/.exec(lines[i]);
        items.push({ depth: Math.floor(m[1].length / 2), ordered: /\d/.test(m[2]), text: m[3] });
        i += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    // Blank
    if (!line.trim()) { i += 1; continue; }

    // Paragraph — consume until a blank line or another block starts
    const para = [];
    while (i < lines.length && lines[i].trim()
           && !/^(#{1,4}\s|```|\s*>|\s*([-*+]|\d+[.)])\s|\s*\|)/.test(lines[i])) {
      para.push(lines[i]); i += 1;
    }
    if (para.length) blocks.push({ type: 'para', text: para.join(' ') });
    else i += 1;
  }
  return blocks;
}

export default function AIMarkdown({ content, className = '' }) {
  const blocks = useMemo(() => parseBlocks(content), [content]);

  return (
    <div className={`ai-md text-[13px] leading-[1.65] text-gray-700 ${className}`}>
      {blocks.map((b, bi) => {
        switch (b.type) {
          case 'heading': {
            const size = { 1: 'text-[16px]', 2: 'text-[15px]', 3: 'text-[14px]', 4: 'text-[13px]' }[b.level];
            return (
              <p key={bi} className={`${size} font-semibold text-gray-900 mt-4 first:mt-0 mb-1.5`}>
                {renderInline(b.text, `h${bi}`)}
              </p>
            );
          }

          case 'list':
            return (
              <ul key={bi} className="my-2 space-y-1">
                {b.items.map((it, ii) => (
                  <li key={ii} className="flex gap-2" style={{ paddingLeft: `${it.depth * 14}px` }}>
                    <span className="shrink-0 mt-[7px] w-[5px] h-[5px] rounded-full bg-[rgba(42,42,114,0.35)]" />
                    <span className="min-w-0">{renderInline(it.text, `l${bi}-${ii}`)}</span>
                  </li>
                ))}
              </ul>
            );

          case 'code':
            return (
              <pre key={bi} className="my-2.5 rounded-[10px] bg-[#f7f8fc] border border-[rgba(42,42,114,0.1)] p-3 overflow-x-auto">
                {b.lang && <span className="block text-[11px] text-gray-400 mb-1.5">{b.lang}</span>}
                <code className="text-[12px] font-mono text-gray-800 whitespace-pre">{b.text}</code>
              </pre>
            );

          case 'table':
            return (
              <div key={bi} className="my-2.5 overflow-x-auto rounded-[10px] border border-[rgba(42,42,114,0.12)]">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-[linear-gradient(160deg,#fbfcfe,#f2f5fd)]">
                      {b.head.map((h, hi) => (
                        <th key={hi} className="text-left font-medium text-gray-600 px-3 py-2 border-b border-[rgba(42,42,114,0.1)]">
                          {renderInline(h, `th${bi}-${hi}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.body.map((row, ri) => (
                      <tr key={ri} className="border-b border-[rgba(42,42,114,0.06)] last:border-0">
                        {row.map((c, ci) => (
                          <td key={ci} className="px-3 py-2 align-top">{renderInline(c, `td${bi}-${ri}-${ci}`)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case 'quote':
            return (
              <blockquote key={bi} className="my-2.5 pl-3 border-l-[3px] border-[rgba(19,56,190,0.35)] text-gray-600">
                {renderInline(b.text, `q${bi}`)}
              </blockquote>
            );

          case 'hr':
            return <hr key={bi} className="my-3 border-0 border-t border-[rgba(42,42,114,0.1)]" />;

          default:
            return (
              <p key={bi} className="my-2 first:mt-0 last:mb-0">{renderInline(b.text, `p${bi}`)}</p>
            );
        }
      })}
    </div>
  );
}
